// Iter 108v — TicketCard extracted from Helpdesk.jsx.
//
// Renders a single ticket as a draggable Kanban card. Kept as a pure
// presentation component: no data fetches, no context hooks — the
// parent Kanban board passes agents + callbacks down. This keeps the
// card cheap to re-render when only one ticket changes.

import { Card } from "@/components/ui/card";
import { User as UserIcon, Trash2, AlarmClock, AlertTriangle, Paperclip } from "lucide-react";
import { fmt, slaCountdown, PRIORITY_TINT, SLA_TONE } from "./helpdeskUtils";

export function TicketCard({ t, agents, onOpen, draggable, onDragStart, canManage, onDelete }) {
  const assignee = agents.find((a) => a.user_id === t.assignee_user_id);
  // Show whichever SLA is most urgent: first-response if unmet, else resolution.
  const primaryDue = !t.first_response_at ? t.first_response_due_at : t.resolution_due_at;
  const doneAt     = !t.first_response_at ? null : (t.resolved_at || null);
  const sla = slaCountdown(primaryDue, doneAt);
  const label = !t.first_response_at ? "1st resp" : "resolve";
  return (
    <Card
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onOpen}
      className="group relative cursor-pointer border-slate-200 p-2.5 text-xs shadow-sm hover:shadow-md"
      data-testid={`helpdesk-card-${t.ticket_id}`}>
      {canManage && (
        <button type="button"
                onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
                title={`Delete ticket ${t.cf_id}`}
                aria-label={`Delete ticket ${t.cf_id}`}
                data-testid={`helpdesk-card-delete-${t.ticket_id}`}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:bg-rose-600 hover:border-rose-600 hover:text-white">
          <Trash2 className="h-3 w-3" />
        </button>
      )}
      <div className="mb-1 flex items-center justify-between">
        <span className="font-mono text-[10px] text-slate-400">{t.cf_id}</span>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_TINT[t.priority] || PRIORITY_TINT.normal}`}>{t.priority}</span>
      </div>
      <div className="mb-2 line-clamp-2 text-sm font-medium text-slate-800">{t.subject}</div>
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="truncate">{t.requester_name || t.requester_email}</span>
        <span className="whitespace-nowrap">{fmt(t.updated_at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-500">
        {assignee ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5">
            <UserIcon className="h-3 w-3" /> {assignee.name}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700">Unassigned</span>
        )}
        {t.team && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-indigo-700">{t.team}</span>
        )}
        {t.attachment_count > 0 && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600"
                title={`${t.attachment_count} attachment${t.attachment_count === 1 ? "" : "s"}`}
                data-testid={`helpdesk-card-attach-${t.ticket_id}`}>
            <Paperclip className="h-3 w-3" /> {t.attachment_count}
          </span>
        )}
        {sla && (
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${SLA_TONE[sla.tone]}`}
                data-testid={`helpdesk-card-sla-${t.ticket_id}`}>
            {sla.tone === "late" ? <AlertTriangle className="h-3 w-3" /> : <AlarmClock className="h-3 w-3" />}
            <span>{label}: {sla.label}</span>
          </span>
        )}
      </div>
    </Card>
  );
}
